import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TypeBadge from '../../components/TypeBadge';
import api from '../../api/ApiClient';

const STATUS = {
  draft:     { label: 'Draft',     color: '#64748B', bg: '#F1F5F9' },
  pending:   { label: 'Pending',   color: '#D97706', bg: '#FFFBEB' },
  verified:  { label: 'Verified',  color: '#1D4ED8', bg: '#EFF6FF' },
  rejected:  { label: 'Rejected',  color: '#DC2626', bg: '#FEF2F2' },
  published: { label: 'Published', color: '#16A34A', bg: '#F0FDF4' },
};

const H  = { padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' };
const TD = { padding: '12px 14px', borderBottom: '1px solid var(--border)' };

function Btn({ onClick, disabled, color = '#64748B', bg = '#F1F5F9', border = '#E2E8F0', children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, background: bg, color, border: `1px solid ${border}`, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap' }}>
      {children}
    </button>
  );
}

// ── New Version Modal ─────────────────────────────────────────────────────────
function VersionModal({ asset, onClose, onDone }) {
  const [changelog, setChangelog] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.createVersion(asset._id, changelog);
      onDone();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: 440, border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--text)' }}>Release New Version</h3>
        <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '0 0 16px' }}>
          Current: <b style={{ color: 'var(--accent)' }}>v{asset.currentVersion || '1.0.0'}</b> → will become <b>v{bumpVersion(asset.currentVersion || '1.0.0')}</b>.
          Current files are snapshotted as the old version.
        </p>
        <label style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>What changed in this version?</label>
        <textarea
          value={changelog} onChange={e => setChangelog(e.target.value)}
          rows={4} placeholder="e.g. Added 50k new rows, fixed label errors, updated schema…"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Releasing…' : 'Release'}
          </button>
        </div>
      </div>
    </div>
  );
}

function bumpVersion(v) {
  const parts = (v || '1.0.0').split('.').map(Number);
  parts[0] += 1;
  return parts.join('.');
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MyAssets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [versionModal, setVersionModal] = useState(null); // asset object

  const load = () => {
    setLoading(true);
    api.getMyAssets().then(d => setAssets(d.assets || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (fn, id) => {
    setActing(id);
    try { await fn(); load(); } catch (e) { alert(e?.response?.data?.message || 'Failed'); }
    setActing(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>My Assets</h1>
        <Link to="/dashboard/upload" className="btn-primary" style={{ fontSize: 13, textDecoration: 'none' }}>+ Create Asset</Link>
      </div>

      {loading && <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading…</div>}

      {!loading && assets.length === 0 && (
        <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12, color: 'var(--border)' }}>—</div>
          <p style={{ color: 'var(--text-2)', marginBottom: 8 }}>No assets yet</p>
          <Link to="/dashboard/upload" style={{ color: 'var(--accent)', fontSize: 14 }}>Create your first asset</Link>
        </div>
      )}

      {!loading && assets.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Title', 'Type', 'Status', 'Views', 'Downloads', 'Rating', 'Price', 'Actions'].map(h => (
                  <th key={h} style={H}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => {
                const st = STATUS[a.status] || STATUS.draft;
                const busy = acting === a._id;
                const isPublished = a.status === 'published';
                const isUnverified = isPublished && !a.verifiedAt;

                return (
                  <tr key={a._id}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseOut={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ ...TD, maxWidth: 200 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'Space Mono', marginTop: 2 }}>v{a.currentVersion || '1.0.0'}</div>
                    </td>
                    <td style={TD}><TypeBadge type={a.assetType} size="sm" /></td>
                    <td style={TD}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600, color: st.color, background: st.bg }}>{st.label}</span>
                        {isUnverified && (
                          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A' }}>unverified</span>
                        )}
                        {a.status === 'rejected' && a.rejectionReason && (
                          <span title={a.rejectionReason} style={{ cursor: 'help', color: 'var(--danger)', fontSize: 13 }}>?</span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...TD, fontSize: 13, fontFamily: 'Space Mono', color: 'var(--text-2)' }}>{a.stats?.views || 0}</td>
                    <td style={{ ...TD, fontSize: 13, fontFamily: 'Space Mono', color: 'var(--text-2)' }}>{a.stats?.downloads || 0}</td>
                    <td style={{ ...TD, fontSize: 13, color: '#D97706' }}>{(a.stats?.rating || 0).toFixed(1)}</td>
                    <td style={{ ...TD, fontSize: 13, fontFamily: 'Space Mono', fontWeight: 700, color: 'var(--text)' }}>
                      {a.price === 0 ? 'Free' : `$${a.price}`}
                    </td>
                    <td style={TD}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minWidth: 180 }}>
                        {/* Preview — opens asset page as users see it */}
                        <Link to={`/asset/${a._id}`} target="_blank"
                          style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, background: 'var(--surface2)', color: 'var(--text-2)', border: '1px solid var(--border)', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Preview
                        </Link>

                        {/* Edit */}
                        <button
                          onClick={() => navigate(`/dashboard/assets/${a._id}/edit`)}
                          style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid #BFDBFE', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          Edit
                        </button>

                        {/* Publish (available for all non-published) */}
                        {!isPublished && (
                          <Btn onClick={() => act(() => api.publishAsset(a._id), a._id)} disabled={busy}
                            bg="#F0FDF4" color="#16A34A" border="#BBF7D0">
                            Publish
                          </Btn>
                        )}

                        {/* Submit for review (draft/rejected only) */}
                        {(a.status === 'draft' || a.status === 'rejected') && (
                          <Btn onClick={() => act(() => api.submitAsset(a._id), a._id)} disabled={busy}
                            bg="#FFFBEB" color="#D97706" border="#FDE68A">
                            Submit
                          </Btn>
                        )}

                        {/* View public page */}
                        {isPublished && (
                          <Link to={`/asset/${a._id}`}
                            style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, background: 'var(--surface2)', color: 'var(--text-2)', border: '1px solid var(--border)', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            View
                          </Link>
                        )}

                        {/* New Version */}
                        <Btn onClick={() => setVersionModal(a)} disabled={busy}
                          bg="#F0FDF4" color="#059669" border="#A7F3D0">
                          + Version
                        </Btn>

                        {/* Delete */}
                        <Btn onClick={() => { if (window.confirm('Delete this asset?')) act(() => api.deleteAsset(a._id), a._id); }}
                          disabled={busy} bg="var(--danger-bg)" color="var(--danger)" border="#FECACA">
                          Delete
                        </Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {versionModal && (
        <VersionModal
          asset={versionModal}
          onClose={() => setVersionModal(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
